// src/pages/EcosystemPage.js
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { canon } from "../lib/seo";

/** HERO háttérkép — ugyanaz, mint a HomePage-en */
const HERO_BG = "/img/t5.webp";

/** Kis képes ikonok az ökoszisztémákhoz (cseréld saját ikonjaidra, ha van) */
const ECO_ICONS = {
  apple: "/img/apple_home_kit_icon.webp",
  google: "/img/google_home_icon.webp",
  alexa: "/img/amazon_alexa_icon.webp",
  matter: "/img/matter_icon.webp",
};
const FALLBACK_ICON = "/img/placeholder-icon.svg";

/** Sima, finom össze/lenyitás – nem „auto”-ra animálunk, hanem mért magasságra */
function Collapse({ open, children, duration = 0.28 }) {
  const ref = useRef(null);
  const [height, setHeight] = useState(0);
  const [render, setRender] = useState(open);

  // ha nyitunk, előbb rendereljük a tartalmat, hogy mérhető legyen
  useEffect(() => {
    if (open) setRender(true);
  }, [open]);

  // mérés minden tartalomváltozáskor
  useLayoutEffect(() => {
    if (!render) return;
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setHeight(el.scrollHeight);
    });
    setHeight(el.scrollHeight);
    ro.observe(el);
    return () => ro.disconnect();
  }, [render, children]);

  const variants = {
    closed: { height: 0, opacity: 0, transition: { duration, ease: [0.22, 1, 0.36, 1] } },
    open: { height, opacity: 1, transition: { duration, ease: [0.22, 1, 0.36, 1] } },
  };

  return (
    <AnimatePresence initial={false} onExitComplete={() => setRender(false)}>
      {(open || render) && (
        <motion.div
          initial="closed"
          animate={open ? "open" : "closed"}
          exit="closed"
          variants={variants}
          style={{ overflow: "hidden" }}
        >
          <div ref={ref}>{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function QAItem({ q, a, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded-xl overflow-hidden">
      <button
        className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="font-medium">{q}</span>
        <span className="ml-3 text-gray-500">{open ? "–" : "+"}</span>
      </button>
      <Collapse open={open}>
        <div className="px-4 py-3 text-sm text-gray-700">{typeof a === "string" ? <p>{a}</p> : a}</div>
      </Collapse>
    </div>
  );
}

/** Részletes adatok + bővített GYIK */
const ECOS = [
  {
    id: "apple",
    title: "Apple Home / HomeKit",
    badge: "HomeKit",
    icon: ECO_ICONS.apple || FALLBACK_ICON,
    desc:
      "Zárt, adatvédelmi fókuszú ökoszisztéma. Gyors lokális vezérlés és egységes élmény iPhone/iPad/Mac/Watch felületeken.",
    pros: [
      "Erős adatvédelem, sok minden megy internet nélkül is",
      "Gyors Otthon app, jó jelenetek és automatizmusok",
      "Thread & Matter támogatás az újabb eszközökben",
    ],
    cons: [
      "Nem minden gyártói extra jelenik meg teljes mélységben",
      "Távoli eléréshez Apple TV / HomePod javasolt (Home Hub)",
    ],
    notes:
      "Vegyes gyártói környezethez a Matter/Thread eszközök adják a legjobb kompatibilitást és jövőállóságot.",
    accent: "bg-emerald-600 hover:bg-emerald-700",
    chip: "bg-emerald-50 border-emerald-200",
    faq: [
      { q: "Kell Home Hub a távoli eléréshez?", a: "Igen: Apple TV vagy HomePod szükséges a stabil távoli vezérléshez és megbízható automatizmusokhoz." },
      { q: "Mi az a Thread és miért jó?", a: "Alacsony fogyasztású mesh okoseszköz-hálózat. Stabil és gyors, nem terheli a Wi-Fi-t, HomeKit/Matter környezetben ideális." },
      { q: "Működik együtt a Philips Hue Bridge-dzsel?", a: "Igen. A Hue Bridge LAN-on kapcsolódik, nagyon stabil és gyors, rengeteg extra funkcióval (zónák, jelenetek)." },
      { q: "Használhatok vegyes márkákat (Aqara, Shelly, Eve…)?", a: "Igen. HomeKit natív és Matter-kompatibilis eszközök gond nélkül keverhetők. Speciális beállításokat a gyártói appban érsz el." },
      { q: "Mi a helyzet a biztonsági kamerákkal?", a: "A HomeKit Secure Video támogatott modellekkel remek. Ellenőrizd, hogy a kamera támogatja-e a HKSV-t." },
    ],
  },
  {
    id: "google",
    title: "Google Home",
    badge: "Google",
    icon: ECO_ICONS.google || FALLBACK_ICON,
    desc:
      "Egyszerű beállítás, kényelmes hangvezérlés (Assistant), széles eszközválaszték. Többfelhasználós modellben erős.",
    pros: [
      "Széles gyártói ökoszisztéma",
      "Letisztult rutinok (automatizációk)",
      "Matter & Thread támogatás folyamatosan bővül",
    ],
    cons: [
      "Haladóbb funkciók néha a gyártói appban maradnak",
      "Időnként UI változások a Google Home appban",
    ],
    notes:
      "Gyártói app + Google Home együtt: finomhangolás az előbbiben, mindennapi használat az utóbbiban.",
    accent: "bg-sky-600 hover:bg-sky-700",
    chip: "bg-sky-50 border-sky-200",
    faq: [
      { q: "Kell külön központ?", a: "Sok Wi-Fi/BT eszköz megy közvetlenül. Thread/Matter-hez érdemes Nest eszközt (Thread Border Router) használni a stabil mesh miatt." },
      { q: "Automatizmusok mennyire lokálisak?", a: "Sok rutin felhős, de a Matter/Thread irány erősödésével egyre több működik lokálisan is." },
      { q: "Philips Hue-t hogyan kössem be?", a: "Hue Bridge-dzsel kapod a legerősebb élményt (LAN, stabilitás, jelenetek). Google Home-mal együtt remekül működik." },
      { q: "Mely eszközök a jó párosok?", a: "Nest, Philips Hue, Aqara (hubbal), TP-Link Tapo, valamint Matter/Thread képes újdonságok. A Builder szűri is ezeket." },
      { q: "Megoszthatom a házat több felhasználóval?", a: "Igen, a Google Home többfelhasználós háztartásban kényelmes jogosultságkezelést ad." },
    ],
  },
  {
    id: "alexa",
    title: "Amazon Alexa",
    badge: "Alexa",
    icon: ECO_ICONS.alexa || FALLBACK_ICON,
    desc:
      "Nagyon erős hangvezérlés és bővíthetőség (skills). Komplex rutinok, széles integrációs kínálat.",
    pros: [
      "Hatalmas eszköz- és skill-ökoszisztéma",
      "Rugalmas, erős rutinok",
      "Matter integráció folyamatosan bővül",
    ],
    cons: [
      "Európában régiófüggő egyes szolgáltatások elérhetősége",
      "Komplexitás miatt néha több finomhangolás kell",
    ],
    notes:
      "Alexa csillog, ha a hangvezérlés és a sok integráció a fő szempont.",
    accent: "bg-indigo-600 hover:bg-indigo-700",
    chip: "bg-indigo-50 border-indigo-200",
    faq: [
      { q: "Kell központ Zigbee/Z-Wave-hez?", a: "Igen, ezekhez általában Alexa-kompatibilis hub/bridge kell. Sok Wi-Fi eszköz viszont megy közvetlenül." },
      { q: "Mennyire okos a hangvezérlés?", a: "Alexa erőssége a hang – összetett parancsok és rutinok is jól kezelhetők, ha rendezett az eszköz- és csoportnév." },
      { q: "Mire figyeljek vásárlásnál?", a: "Alexa/Matter kompatibilitás; ha Zigbee/Z-Wave kell, legyen megfelelő hub. A Builder mutatja a kompatibilitást." },
      { q: "Mi a helyzet a több nyelvvel?", a: "Alexán több nyelvi beállítás is lehetséges, de funkciók régiótól függhetnek." },
      { q: "Hue és Alexa együtt?", a: "Hue Bridge-dzsel stabil, gyors és funkciógazdag; Alexa rutinokkal jól kombinálható." },
    ],
  },
  {
    id: "matter",
    title: "Matter / Thread-központú",
    badge: "Matter",
    icon: ECO_ICONS.matter || FALLBACK_ICON,
    desc:
      "Gyártófüggetlen szabvány a jobb együttműködésért. A cél: ugyanaz az eszköz több platformon is jól működjön.",
    pros: [
      "Többplatformos, jövőálló",
      "Lokális, gyors vezérlés Thread-del",
      "Egységes párosítás és alapprofilok",
    ],
    cons: [
      "Átmeneti időszak: vegyes élmény előfordulhat",
      "Régebbi eszközök gyakran Bridge-et igényelnek",
    ],
    notes:
      "Kezdőknek és vegyes márkát tervezőknek kifejezetten jó alapot ad a Matter/Thread.",
    accent: "bg-gray-800 hover:bg-gray-900",
    chip: "bg-gray-100 border-gray-300",
    faq: [
      { q: "Kell központ a Matterhez?", a: "Sok eszköz közvetlenül működik a választott ökoszisztémával. Thread-hez kell egy Thread Border Router (HomePod, Apple TV 4K, Nest, stb.)." },
      { q: "Minden Matter eszköz ugyanazt tudja minden platformon?", a: "A cél ez, de a funkciók mélysége eltérhet. A Builderrel ellenőrizheted a támogatást és leszűrheted a listát." },
      { q: "Érdemes megtartani a hidakat (Hue Bridge, stb.)?", a: "Gyakran igen: stabilitás, LAN-lokalitás, extra funkciók miatt." },
      { q: "Mi az a Thread és miben más a Zigbee-től?", a: "Mindkettő mesh, de a Thread IP-alapú (IPv6), így könnyebb többplatformos integrációkat építeni." },
      { q: "Hogyan tervezek vele jövőállót?", a: "Válassz Matter/Thread képes eszközöket, és adj elsőbbséget a széles körben támogatott márkáknak." },
    ],
  },
];

/** Összehasonlító mátrix */
const MATRIX = [
  { feat: "Lokális vezérlés", apple: "✓✓", google: "✓", alexa: "✓", matter: "✓✓" },
  { feat: "Hangvezérlés minősége", apple: "jó", google: "jó", alexa: "kiváló", matter: "–" },
  { feat: "Többplatformos kompat.", apple: "közepes", google: "közepes", alexa: "közepes", matter: "kiváló" },
  { feat: "Automatizmusok", apple: "kiváló", google: "jó", alexa: "kiváló", matter: "alap + ökoszisztémától függ" },
  { feat: "Kezdőknek", apple: "könnyű", google: "könnyű", alexa: "közepes", matter: "könnyű/közepes" },
];

function EcoCard({ eco, expanded, onToggle, onChoose }) {
  return (
    <motion.div
      layout
      className="bg-white rounded-2xl border border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md transition-all p-5"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 shrink-0 rounded-xl border bg-gray-50 flex items-center justify-center overflow-hidden">
          <img
            src={eco.icon || FALLBACK_ICON}
            alt={`${eco.title} ikon`}
            className="w-8 h-8 object-contain"
            loading="lazy"
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-xl font-semibold">{eco.title}</h3>
            <span className={`text-xs px-2 py-0.5 rounded border ${eco.chip}`}>{eco.badge}</span>
          </div>

          <p className="text-sm text-gray-700 mt-1">{eco.desc}</p>

          {/* Előnyök / Hátrányok */}
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border rounded-xl p-3 bg-emerald-50/40">
              <div className="text-sm font-medium mb-1">Előnyök</div>
              <ul className="text-sm text-gray-800 list-disc pl-5 space-y-1">
                {eco.pros.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
            <div className="border rounded-xl p-3 bg-rose-50/40">
              <div className="text-sm font-medium mb-1">Hátrányok</div>
              <ul className="text-sm text-gray-800 list-disc pl-5 space-y-1">
                {eco.cons.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          </div>

          <p className="text-xs text-gray-600 mt-2">{eco.notes}</p>

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              className={`${eco.accent} text-white px-4 py-2 rounded-lg`}
              onClick={() => onChoose(eco.id)}
            >
              Ezt használom
            </button>
            <button
              className="px-4 py-2 rounded-lg border hover:bg-gray-50"
              onClick={onToggle}
              aria-expanded={expanded}
            >
              GYIK / részletek
            </button>
          </div>

          {/* GYIK – simább animációval */}
          <Collapse open={expanded}>
            <div className="mt-4 space-y-3">
              {eco.faq.map((item, idx) => (
                <QAItem key={idx} q={item.q} a={item.a} />
              ))}
            </div>
          </Collapse>
        </div>
      </div>
    </motion.div>
  );
}

export default function EcosystemPage() {
  const navigate = useNavigate();
  const [openId, setOpenId] = useState(null);
  const { pathname } = useLocation();

  const handleChoose = (ecoId) => {
    sessionStorage.setItem("shb:ecosystem", ecoId);
    navigate("/builder");
  };

  const currentEco = useMemo(() => sessionStorage.getItem("shb:ecosystem") || "", []);

  return (
    <>
      {/* SEO – Ecosystems */}
      <title>Ökoszisztémák – Apple Home, Google Home, Amazon Alexa, Matter | Solarya</title>
      <meta
        name="description"
        content="Válaszd ki az ökoszisztémád: Apple Home (HomeKit), Google Home, Amazon Alexa vagy Matter/Thread. Előnyök, hátrányok, GYIK és ajánlások."
      />
      <link rel="canonical" href={canon(pathname)} />

      <meta property="og:type" content="website" />
      <meta property="og:title" content="Ökoszisztémák – Apple/Google/Alexa/Matter | Solarya" />
      <meta
        property="og:description"
        content="Előnyök, hátrányok, GYIK és ajánlások az okosotthon ökoszisztémákhoz."
      />
      <meta property="og:url" content={canon(pathname)} />


      <motion.div
        className="text-[#0c1a26]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
      >
        {/* =================== HERO (HomePage-stílus) =================== */}
        <section className="relative overflow-hidden">
          {/* ugyanaz a sötét átmenet, mint a HomePage-en */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0c1a26] to-[#173047]" />
          {/* teljes hátteres kép, 25% átlátszósággal */}
          <div className="absolute inset-0 opacity-25">
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `url(${"/img/t5.webp"})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          </div>

          {/* tartalom */}
          <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-3">
              Válaszd ki az ökoszisztémád
            </h1>
            <p className="text-white/90 text-lg md:text-xl max-w-2xl">
              Az ökoszisztéma határozza meg a kompatibilitást, a hangvezérlést és az
              automatizmusok minőségét. Válassz: Apple Home, Google Home, Amazon Alexa
              vagy Matter/Thread alap.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                onClick={() => navigate("/builder")}
                className="inline-flex items-center justify-center rounded-xl bg-blue-600 hover:bg-blue-700 px-5 py-3 font-semibold"
              >
                Kezdjük az építést
              </button>
              <button
                onClick={() => {
                  const el = document.getElementById("ecos-list");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="inline-flex items-center justify-center rounded-xl border border-white/40 hover:bg-white hover:text-[#0c1a26] px-5 py-3 font-semibold"
              >
                Ökoszisztémák megtekintése
              </button>
            </div>
          </div>
        </section>

        {/* === Ökoszisztéma kártyák + GYIK === */}
        <div id="ecos-list" className="p-6 max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
            {ECOS.map((eco) => (
              <EcoCard
                key={eco.id}
                eco={eco}
                expanded={openId === eco.id}
                onToggle={() => setOpenId((v) => (v === eco.id ? null : eco.id))}
                onChoose={handleChoose}
              />
            ))}
          </div>

          {/* Összehasonlító mátrix */}
          <section className="bg-white border rounded-2xl p-5 mb-8">
            <h2 className="text-xl font-semibold mb-3">Gyors összehasonlítás</h2>
            <div className="overflow-x-auto">
              <table className="min-w-[700px] w-full text-sm">
                <thead>
                  <tr className="text-left">
                    <th className="p-3 border-b">Jellemző</th>
                    <th className="p-3 border-b">Apple</th>
                    <th className="p-3 border-b">Google</th>
                    <th className="p-3 border-b">Alexa</th>
                    <th className="p-3 border-b">Matter</th>
                  </tr>
                </thead>
                <tbody>
                  {MATRIX.map((row, idx) => (
                    <tr key={idx} className="odd:bg-gray-50/40">
                      <td className="p-3 border-b font-medium">{row.feat}</td>
                      <td className="p-3 border-b">{row.apple}</td>
                      <td className="p-3 border-b">{row.google}</td>
                      <td className="p-3 border-b">{row.alexa}</td>
                      <td className="p-3 border-b">{row.matter}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              A táblázat iránymutató: a pontos funkciók a konkrét eszközök és központok képességeitől függenek.
            </p>
          </section>

          {/* Gyors kezdés – UX edukáció */}
          <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            {[
              {
                t: "1) Ökoszisztéma kiválasztása",
                d: "Válaszd ki a számodra legkényelmesebb platformot. Később is módosíthatod.",
              },
              {
                t: "2) Funkciók bepipálása",
                d: "Világítás, árnyékolás, fűtés, érzékelők… jelöld, mire van szükséged.",
              },
              {
                t: "3) Termékek & HUB ajánló",
                d: "Válogass kompatibilis termékekből. A végén központ-javaslat és összegzés érkezik.",
              },
            ].map((s, i) => (
              <div key={i} className="bg-white border rounded-2xl p-5">
                <div className="text-2xl font-bold mb-2">{s.t}</div>
                <p className="text-sm text-gray-700">{s.d}</p>
              </div>
            ))}
          </section>

          {/* Általános GYIK – bővítve */}
          <section className="bg-white border rounded-2xl p-5 mb-16">
            <h2 className="text-xl font-semibold mb-3">Gyakori kérdések</h2>
            <div className="space-y-3">
              <QAItem
                q="Mi történik, ha később ökoszisztémát váltok?"
                a="A Matter/Thread eszközök több platformon is működnek, de a funkciók mélysége eltérhet. A Builderrel újratervezheted a kombinációkat."
              />
              <QAItem
                q="Szükségem van biztosan HUB-ra?"
                a="Nem mindig. Wi-Fi/BT eszközök központ nélkül is működhetnek; Zigbee/Z-Wave/Thread esetén jellemzően javasolt. A Builder HUB-ajánlója segít."
              />
              <QAItem
                q="Megmaradnak a választásaim?"
                a="Igen. A választások (ökoszisztéma, szűrők) böngésző szinten (sessionStorage/localStorage) ideiglenesen megmaradnak."
              />
              <QAItem
                q="Mi a különbség a Thread és a Zigbee között?"
                a="Mindkettő mesh hálózat, de a Thread IP-alapú (IPv6), így natívabb többplatformos integrációk érhetők el."
              />
              <QAItem
                q="Mit tegyek, ha több márkát szeretnék keverni?"
                a="Előnyben a Matter/Thread képes eszközök. A Builder szűrői és kompatibilitás jelzői segítenek stabil mixet építeni."
              />
            </div>
          </section>
        </div>

        {/* Sticky segédsáv – aktuális ökoszisztéma + CTA */}
        <StickyHelper />
      </motion.div>
    </>
  );
}

/** Sticky helper külön komponensben – görgetésnél is kéznél */
function StickyHelper() {
  const navigate = useNavigate();
  const [eco, setEco] = useState("");

  useEffect(() => {
    const v = sessionStorage.getItem("shb:ecosystem") || "";
    setEco(v);
    const onStorage = () => setEco(sessionStorage.getItem("shb:ecosystem") || "");
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const label =
    eco === "apple"
      ? "Apple Home"
      : eco === "google"
        ? "Google Home"
        : eco === "alexa"
          ? "Amazon Alexa"
          : eco === "matter"
            ? "Matter/Thread"
            : "";

  if (!eco) return null;

  return (
    <div className="fixed bottom-4 left-0 right-0 z-40">
      <div className="max-w-6xl mx-auto px-6">
        <div className="rounded-2xl border bg-white/90 backdrop-blur shadow-md p-3 flex items-center justify-between gap-3">
          <div className="text-sm">
            <span className="text-gray-600 mr-2">Előválasztott ökoszisztéma:</span>
            <span className="font-medium">{label}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-4 py-2 rounded border hover:bg-gray-50 text-sm"
              onClick={() => {
                sessionStorage.removeItem("shb:ecosystem");
                window.dispatchEvent(new Event("storage"));
              }}
            >
              Törlés
            </button>
            <button
              className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm"
              onClick={() => navigate("/builder")}
            >
              Tovább a Builderhez
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
